import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ClipboardCheck, Plus, Play, CheckCircle2, XCircle, Pause, Trash2, Edit } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  planned: 'bg-info/20 text-info',
  in_progress: 'bg-warning/20 text-warning',
  completed: 'bg-success/20 text-success',
  on_hold: 'bg-muted text-foreground',
  cancelled: 'bg-error/20 text-error',
};

export default function ProductionOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [boms, setBoms] = useState([]);
  const [machines, setMachines] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ product_name: '', bom_id: '', quantity: '', unit: 'pcs', priority: 'normal', assigned_machine: '', start_date: '', due_date: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [ordersRes, bomsRes, machinesRes] = await Promise.all([
        api.get('/factory/production-orders'),
        api.get('/factory/bom'),
        api.get('/factory/machines'),
      ]);
      setOrders(ordersRes.data);
      setBoms(bomsRes.data);
      setMachines(machinesRes.data);
    } catch { toast.error('Failed to load data'); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    if (!form.product_name) return toast.error('Product name required');
    if (!form.quantity) return toast.error('Quantity required');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/factory/production-orders/${editing}`, { ...form, quantity: parseFloat(form.quantity) });
        toast.success('Order updated');
      } else {
        await api.post('/factory/production-orders', { ...form, quantity: parseFloat(form.quantity) });
        toast.success('Production order created');
      }
      setShowDialog(false);
      setEditing(null);
      setForm({ product_name: '', bom_id: '', quantity: '', unit: 'pcs', priority: 'normal', assigned_machine: '', start_date: '', due_date: '', notes: '' });
      fetchData();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status, extra = {}) => {
    try {
      await api.put(`/factory/production-orders/${id}/status`, { status, ...extra });
      toast.success(`Status updated to ${status}`);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this order?')) return;
    try {
      await api.delete(`/factory/production-orders/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleEdit = (order) => {
    setEditing(order.id);
    setForm({
      product_name: order.product_name || '',
      bom_id: order.bom_id || '',
      quantity: order.quantity || '',
      unit: order.unit || 'pcs',
      priority: order.priority || 'normal',
      assigned_machine: order.assigned_machine || '',
      start_date: order.start_date || '',
      due_date: order.due_date || '',
      notes: order.notes || '',
    });
    setShowDialog(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="production-orders-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <ClipboardCheck size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="production-orders-title">Production Orders</h1>
              <p className="text-[13px] text-muted-foreground mt-0.5 font-medium">Manage manufacturing production orders</p>
            </div>
          </div>
          <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) { setEditing(null); setForm({ product_name: '', bom_id: '', quantity: '', unit: 'pcs', priority: 'normal', assigned_machine: '', start_date: '', due_date: '', notes: '' }); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-xl h-9 text-[13px]" data-testid="new-order-btn"><Plus size={14} className="mr-1.5" />New Order</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="production-order-dialog">
              <DialogHeader><DialogTitle className="font-outfit">{editing ? 'Edit' : 'New'} Production Order</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label className="text-[12px]">Product Name *</Label><Input value={form.product_name} onChange={e => setForm({...form, product_name: e.target.value})} placeholder="Product name" data-testid="order-product-name" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  <div><Label className="text-[12px]">Quantity *</Label><Input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} data-testid="order-quantity" /></div>
                  <div><Label className="text-[12px]">Unit</Label>
                    <Select value={form.unit} onValueChange={v => setForm({...form, unit: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['pcs', 'kg', 'liters', 'meters', 'tons', 'boxes'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[12px]">Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['low', 'normal', 'high', 'urgent'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {boms.length > 0 && (
                  <div><Label className="text-[12px]">Bill of Materials</Label>
                    <Select value={form.bom_id} onValueChange={v => setForm({...form, bom_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select BOM" /></SelectTrigger>
                      <SelectContent>{boms.map(b => <SelectItem key={b.id} value={b.id}>{b.product_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {machines.length > 0 && (
                  <div><Label className="text-[12px]">Assign Machine</Label>
                    <Select value={form.assigned_machine} onValueChange={v => setForm({...form, assigned_machine: v})}>
                      <SelectTrigger><SelectValue placeholder="Select machine" /></SelectTrigger>
                      <SelectContent>{machines.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[12px]">Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
                  <div><Label className="text-[12px]">Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
                </div>
                <div><Label className="text-[12px]">Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
                <Button onClick={handleSave} disabled={saving} className="w-full rounded-xl">{saving ? 'Saving...' : editing ? 'Update Order' : 'Create Order'}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total', value: orders.length, color: 'text-foreground' },
            { label: 'Planned', value: orders.filter(o => o.status === 'planned').length, color: 'text-info' },
            { label: 'In Progress', value: orders.filter(o => o.status === 'in_progress').length, color: 'text-warning' },
            { label: 'Completed', value: orders.filter(o => o.status === 'completed').length, color: 'text-success' },
            { label: 'On Hold', value: orders.filter(o => o.status === 'on_hold').length, color: 'text-muted-foreground' },
          ].map(s => (
            <Card key={s.label} className="stat-card border-0 bg-white">
              <CardContent className="pt-4 pb-3.5 px-4">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">{s.label}</p>
                <p className={`text-2xl font-bold font-outfit mt-1 ${s.color}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Orders Table */}
        <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl overflow-hidden">
          <CardContent className="px-0 pt-4">
            {orders.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <ClipboardCheck size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-[13px]">No production orders. Create your first one above.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="production-orders-table">
                  <thead><tr>
                    <th className="text-left">Order #</th>
                    <th className="text-left">Product</th>
                    <th className="text-right">Qty</th>
                    <th className="text-center">Priority</th>
                    <th className="text-center">Status</th>
                    <th className="text-left">Due Date</th>
                    <th className="text-right">Actions</th>
                  </tr></thead>
                  <tbody>
                    {orders.map(order => (
                      <tr key={order.id} className="group" data-testid="production-order-row">
                        <td className="font-mono text-[12px] font-semibold text-primary">{order.order_number}</td>
                        <td className="font-medium text-foreground">{order.product_name}</td>
                        <td className="text-right font-semibold tabular-nums">{order.quantity} {order.unit}</td>
                        <td className="text-center">
                          <Badge className={`text-[10px] capitalize ${order.priority === 'urgent' ? 'bg-error/20 text-error' : order.priority === 'high' ? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'}`}>{order.priority}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge className={`${STATUS_COLORS[order.status] || 'bg-muted'} text-[10px] capitalize`}>{order.status?.replace('_', ' ')}</Badge>
                        </td>
                        <td className="text-muted-foreground">{order.due_date || '-'}</td>
                        <td className="text-right">
                          <div className="flex gap-1 justify-end opacity-80 group-hover:opacity-100">
                            {order.status === 'planned' && (
                              <Button size="sm" variant="ghost" className="h-7 text-[11px] text-info" onClick={() => updateStatus(order.id, 'in_progress')}><Play size={12} className="mr-1" />Start</Button>
                            )}
                            {order.status === 'in_progress' && (
                              <>
                                <Button size="sm" variant="ghost" className="h-7 text-[11px] text-success" onClick={() => updateStatus(order.id, 'completed', { completed_qty: order.quantity })}><CheckCircle2 size={12} className="mr-1" />Done</Button>
                                <Button size="sm" variant="ghost" className="h-7 text-[11px] text-muted-foreground" onClick={() => updateStatus(order.id, 'on_hold')}><Pause size={12} className="mr-1" />Hold</Button>
                              </>
                            )}
                            {order.status === 'on_hold' && (
                              <Button size="sm" variant="ghost" className="h-7 text-[11px] text-info" onClick={() => updateStatus(order.id, 'in_progress')}><Play size={12} className="mr-1" />Resume</Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => handleEdit(order)}><Edit size={12} /></Button>
                            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-error" onClick={() => handleDelete(order.id)}><Trash2 size={12} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
